import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { AlertTriangle, RefreshCw, Settings, Zap, CheckCircle, XCircle, Clock, Activity } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface FailedBill {
  id: string;
  file_name: string;
  error_message: string;
  failure_count: number;
  last_attempt: string;
  error_category: string;
  recovery_status: 'pending' | 'in_progress' | 'recovered' | 'failed';
  auto_recoverable: boolean;
}

interface RecoveryStrategy {
  id: string;
  name: string;
  description: string;
  success_rate: number;
  avg_time: number;
  error_categories: string[];
}

const EnhancedErrorRecoverySystem = () => {
  const [autoRecoveryEnabled, setAutoRecoveryEnabled] = useState(true);
  const [selectedBills, setSelectedBills] = useState<string[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch failed bills
  const { data: failedBills, isLoading: billsLoading } = useQuery({
    queryKey: ['failed-bills'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bill_upload_queue')
        .select('*')
        .eq('processing_status', 'failed')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data.map(bill => ({
        id: bill.id,
        file_name: bill.file_name,
        error_message: bill.processing_error || 'Unknown error',
        failure_count: 1,
        last_attempt: bill.updated_at,
        error_category: categorizeError(bill.processing_error || ''),
        recovery_status: 'pending' as const,
        auto_recoverable: isAutoRecoverable(bill.processing_error || '')
      }));
    },
    refetchInterval: 15000
  });

  // Fetch recovery strategies
  const { data: strategies } = useQuery({
    queryKey: ['recovery-strategies'],
    queryFn: async () => {
      // Mock data for strategies
      return [
        {
          id: 'api-retry',
          name: 'API Retry with Backoff',
          description: 'Retries failed API calls with exponential backoff',
          success_rate: 85,
          avg_time: 30,
          error_categories: ['api_error', 'timeout']
        },
        {
          id: 'alternative-parser',
          name: 'Alternative Parser Selection',
          description: 'Switch to a different AI parser when one fails',
          success_rate: 78,
          avg_time: 45,
          error_categories: ['api_error', 'parsing_error']
        },
        {
          id: 'manual-review',
          name: 'Manual Review Queue',
          description: 'Send complex cases to manual review',
          success_rate: 95,
          avg_time: 1800,
          error_categories: ['validation_error', 'complex_format']
        },
        {
          id: 'format-conversion',
          name: 'Format Conversion',
          description: 'Convert file to alternative format for processing',
          success_rate: 72,
          avg_time: 60,
          error_categories: ['file_error', 'format_error']
        }
      ] as RecoveryStrategy[];
    }
  });

  // Bulk recovery mutation
  const bulkRecoveryMutation = useMutation({
    mutationFn: async (billIds: string[]) => {
      const { data, error } = await supabase.functions.invoke('bulk-error-recovery', {
        body: { billIds, autoRecovery: autoRecoveryEnabled }
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Recovery Initiated",
        description: "Bulk recovery process has been started for selected bills.",
      });
      queryClient.invalidateQueries({ queryKey: ['failed-bills'] });
      setSelectedBills([]);
    },
    onError: (error) => {
      toast({
        title: "Recovery Failed",
        description: "Failed to initiate bulk recovery process.",
        variant: "destructive"
      });
    }
  });

  // Individual recovery mutation
  const individualRecoveryMutation = useMutation({
    mutationFn: async (billId: string) => {
      const { data, error } = await supabase.functions.invoke('individual-error-recovery', {
        body: { billId }
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Recovery Initiated",
        description: "Individual recovery process has been started.",
      });
      queryClient.invalidateQueries({ queryKey: ['failed-bills'] });
    }
  });

  function categorizeError(errorMessage: string): string {
    const message = errorMessage.toLowerCase();
    
    if (message.includes('api') || message.includes('401') || message.includes('402') || message.includes('403')) {
      return 'api_error';
    }
    if (message.includes('timeout') || message.includes('network')) {
      return 'timeout';
    }
    if (message.includes('parse') || message.includes('json') || message.includes('format')) {
      return 'parsing_error';
    }
    if (message.includes('file') || message.includes('upload') || message.includes('size')) {
      return 'file_error';
    }
    if (message.includes('validation') || message.includes('required') || message.includes('invalid')) {
      return 'validation_error';
    }
    
    return 'unknown';
  }

  function isAutoRecoverable(errorMessage: string): boolean {
    const category = categorizeError(errorMessage);
    return ['api_error', 'timeout', 'parsing_error'].includes(category);
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'api_error': return 'destructive';
      case 'timeout': return 'outline';
      case 'parsing_error': return 'secondary';
      case 'file_error': return 'destructive';
      case 'validation_error': return 'outline';
      default: return 'secondary';
    }
  };

  const autoRecoverableBills = failedBills?.filter(bill => bill.auto_recoverable) || [];
  const manualReviewBills = failedBills?.filter(bill => !bill.auto_recoverable) || [];

  return (
    <div className="space-y-6">
      {/* Control Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <span>Recovery Settings</span>
          </CardTitle>
          <CardDescription>
            Configure automatic error recovery and monitoring
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Auto Recovery</Label>
              <div className="text-sm text-muted-foreground">
                Automatically attempt to recover from certain types of errors
              </div>
            </div>
            <Switch
              checked={autoRecoveryEnabled}
              onCheckedChange={setAutoRecoveryEnabled}
            />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-destructive">{failedBills?.length || 0}</div>
              <div className="text-sm text-muted-foreground">Total Failed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-warning">{autoRecoverableBills.length}</div>
              <div className="text-sm text-muted-foreground">Auto Recoverable</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-secondary">{manualReviewBills.length}</div>
              <div className="text-sm text-muted-foreground">Manual Review</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-success">
                {strategies?.reduce((sum, s) => sum + s.success_rate, 0) / (strategies?.length || 1) || 0}%
              </div>
              <div className="text-sm text-muted-foreground">Avg Success Rate</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="failed-bills" className="space-y-4">
        <TabsList>
          <TabsTrigger value="failed-bills">Failed Bills</TabsTrigger>
          <TabsTrigger value="strategies">Recovery Strategies</TabsTrigger>
          <TabsTrigger value="history">Recovery History</TabsTrigger>
        </TabsList>

        <TabsContent value="failed-bills" className="space-y-4">
          {/* Bulk Actions */}
          {selectedBills.length > 0 && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {selectedBills.length} bills selected
                  </div>
                  <div className="space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => setSelectedBills([])}
                    >
                      Clear Selection
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Bulk Recovery
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Confirm Bulk Recovery</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will attempt to recover {selectedBills.length} failed bills using appropriate recovery strategies. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => bulkRecoveryMutation.mutate(selectedBills)}
                          >
                            Start Recovery
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Failed Bills List */}
          <div className="space-y-3">
            {billsLoading ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <RefreshCw className="h-8 w-8 mx-auto mb-2 animate-spin text-muted-foreground" />
                  <p className="text-muted-foreground">Loading failed bills...</p>
                </CardContent>
              </Card>
            ) : failedBills?.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-success" />
                  <p className="text-muted-foreground">No failed bills found!</p>
                </CardContent>
              </Card>
            ) : (
              failedBills?.map((bill) => (
                <Card key={bill.id} className="transition-colors hover:bg-muted/50">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedBills.includes(bill.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedBills([...selectedBills, bill.id]);
                            } else {
                              setSelectedBills(selectedBills.filter(id => id !== bill.id));
                            }
                          }}
                          className="mt-1"
                        />
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-medium">{bill.file_name}</h4>
                            <Badge variant={getCategoryColor(bill.error_category)}>
                              {bill.error_category.replace('_', ' ')}
                            </Badge>
                            {bill.auto_recoverable && (
                              <Badge variant="secondary">
                                <Zap className="h-3 w-3 mr-1" />
                                Auto Recoverable
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{bill.error_message}</p>
                          <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                            <span>Attempts: {bill.failure_count}</span>
                            <span>Last: {new Date(bill.last_attempt).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => individualRecoveryMutation.mutate(bill.id)}
                          disabled={individualRecoveryMutation.isPending}
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Retry
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="strategies" className="space-y-4">
          <div className="grid gap-4">
            {strategies?.map((strategy) => (
              <Card key={strategy.id}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{strategy.name}</span>
                    <Badge variant="outline">{strategy.success_rate}% Success</Badge>
                  </CardTitle>
                  <CardDescription>{strategy.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span>Success Rate</span>
                      <span>{strategy.success_rate}%</span>
                    </div>
                    <Progress value={strategy.success_rate} />
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Avg Time:</span>
                        <span className="ml-2">{strategy.avg_time}s</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Error Types:</span>
                        <span className="ml-2">{strategy.error_categories.length}</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-1">
                      {strategy.error_categories.map((category) => (
                        <Badge key={category} variant="secondary" className="text-xs">
                          {category.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recovery History</CardTitle>
              <CardDescription>
                Track of all recovery attempts and their outcomes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Activity className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-muted-foreground">Recovery history will be displayed here</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Implementation pending - will show historical recovery data
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EnhancedErrorRecoverySystem;